import { lazy, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  EMPTY_HOVER_LEGEND,
  getHoverOwner,
  hoverLegendBaseAtom,
  setWidgetHoverAtom,
  widgetHoverAtom
} from "atoms/interaction";
import classnames from "classnames";
import { DEFAULT_MIDSTREAM_SETTINGS } from "constants/app.constants";
import {
  AXIS_TYPE,
  DEFAULT_CHART_PRODUCT,
  DEFAULT_MIDSTREAM_CHART_PRODUCT,
  IS_BOX_PLOT_CHART
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { EntityType } from "constants/entities.enum";
import { MIDSTREAM_CHART_BACKGROUND_COLOUR } from "constants/style.constants";
import { ChartStatus, useChartsContext } from "contexts/ChartContext";
import { UNDEFINED_LOCKS, UNDEFINED_MPC_LOCKS } from "entities/charts/chart";
import {
  createFeatureEntities,
  restoreNormalizationUnitsSentinel
} from "entities/charts/eva/features";
import { createEntityState } from "entities/charts/factory";
import { useAtomValue, useSetAtom } from "jotai";
import debounce from "lodash/debounce";
import { createChart, removeChart, updateChart } from "store/features/charts/chartSlice";
import { useIsDashboardScreenshotMode } from "stores/dashboardScreenshot/useIsDashboardScreenshotMode";
import { selectHasTypeWellSaveSucceeded, useForecastStore } from "stores/forecast";
import styled from "styled-components/macro";
import { disableAxisLocks, getProductType, noop, retry } from "utils";

import { useChartDependencies, useChartEntities } from "hooks/charts";
import useChartSize from "hooks/charts/echart/useChartSize";

import { EntityKind } from "models/entityKind";

import { ErrorBoundary } from "components/base";
import useTypeWellSeries from "components/charts/hooks/useTypeWellSeries";
import { useUserSettings } from "components/user/hooks";

import { ResultMessageType } from "../../../models/model";
import { RootState } from "../../../store/rootReducer";
import Indicator from "../../ui/Indicator";
import useChartModificationTracker from "../hooks/useChartModificationTracker";
import { useTypeWellNormalizationToasts } from "../hooks/useTypeWellNormalizationToasts";
import ChartLegend from "../legend/Legend";
import { AxisContainer } from "./AxisContainer/AxisContainer";
import BoxPlotBinSettingsToggle from "./BoxPlotBinSettingsToggle";
import { ChartAutomationHandler } from "./ChartAutomationHandler";
import ChartAxisFieldSelector from "./ChartAxisFieldSelector";
import ChartToolbar from "./ChartToolbar";
import "./ChartWrapper.scss";
import CopyUwiContextMenu from "./CopyUwiContextMenu";
import { checkProductExists } from "./ProductSelector/utils/checkProductExists";
import Screenshot from "./Screenshot";
import TypeWellCutoffToastNotifier from "./TypeWellCutoffToastNotifier";
import WellCountToggle from "./WellCountToggle";

const Chart = lazy(() => retry(() => import("./Chart")));
interface ChartWrapperT {
  className?: string;
  onFullscreen?: (b: boolean) => void;
}
function ChartWrapper({ className = "", onFullscreen = noop }: ChartWrapperT) {
  // TODO chart: Potential performance bottleneck based on profiler. Needs more investigation
  const { midstreamChartBackgroundColour, midstreamSettings } = useUserSettings();
  const midstreamEnabled = midstreamSettings?.enabled ?? DEFAULT_MIDSTREAM_SETTINGS;
  const { id, entityKind, status, initialChartSettings } = useChartsContext();
  const wrapperRef = useRef(null);
  const noDataIndicatorRef = useRef(false);
  const wellsHiddenIndicatorRef = useRef(false);
  const prevInitialSettingsRef = useRef(null);

  const rootClassnames = classnames("chart-wrapper", className);
  const screenshotContainerId = `screenshot-container-${id}`;
  const dispatch = useDispatch();
  const {
    chartType: entityChartType,
    fullScreen,
    screenshot,
    chartLegend,
    bounds,
    dataTable,
    typewells,
    normalizeBy,
    apiResponse,
    product: entityProduct
  } = useChartEntities(id);
  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const setHoverLegend = useSetAtom(hoverLegendBaseAtom);
  const setWidgetHover = useSetAtom(setWidgetHoverAtom);
  const isHover = widgetHoverMap?.[id] ?? false;

  // Debounced safety-net: clear hover state when the pointer leaves the chart
  // wrapper boundary. Without debouncing, rapid DOM mouseleave/mouseenter cycles
  // (e.g. moving between adjacent legend items) fire Jotai writes at pointer
  // frequency, triggering re-renders across every chart subscriber per frame.
  // 150 ms is longer than inter-item transition time but well under perceivable
  // highlight latency. setHoverLegend is a stable reference from useSetAtom.
  const debouncedClearHoverLegend = useMemo(
    () =>
      debounce(() => {
        if (getHoverOwner() !== "chart") return;
        setHoverLegend(EMPTY_HOVER_LEGEND);
      }, 150),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // setHoverLegend is stable from useSetAtom — empty deps is intentional
  );
  useEffect(
    () => () => {
      debouncedClearHoverLegend.cancel();
    },
    [debouncedClearHoverLegend]
  );

  const { checkedGlobalTypeWells, globalNormalizeBy, useNormalizeBy, normalizeTypeWell } =
    useChartDependencies(entityKind);

  const hasTypeWellSaveSucceeded = useForecastStore(selectHasTypeWellSaveSucceeded);

  const { typeWellSeries, numberOfTypeWellsMissingNormalization } = useTypeWellSeries(
    checkedGlobalTypeWells,
    normalizeBy?.active && normalizeBy?.properties
      ? normalizeBy.properties
      : globalNormalizeBy,
    normalizeBy?.active,
    normalizeTypeWell,
    typewells?.active
  );

  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);

  const countExceeded =
    apiResponse?.messageType === ResultMessageType.WellCountExceeded ||
    apiResponse?.messageType === ResultMessageType.SeriesCountExceeded;

  // Backend/gRPC failures (e.g. eva-data-svc-rs's GroupBy ceiling on an
  // OverrideSizeLimit retry) come back as GeneralError with an actionable
  // Message -- surface that instead of folding it into "No Data Available",
  // which tells the user their filter is empty rather than what to do about
  // it. Same shape as WellStatusChartWrapper.tsx's isServerError/serverErrorMessage.
  const isServerError = apiResponse?.messageType === ResultMessageType.GeneralError;
  const serverErrorMessage = apiResponse?.messages?.find((m) => m.message)?.message;

  const isMissingData =
    // GeneralError has empty Series like a genuine no-data result, so it has to be
    // excluded explicitly here, not just from the messageType clause below -- it
    // gets its own indicator branch (isServerError) instead.
    !isServerError &&
    // apiResponse needs to be existed for missing data
    ((apiResponse &&
      apiResponse.series &&
      apiResponse.series.length === 0 &&
      typeWellSeries.length === 0) ||
      (apiResponse &&
        apiResponse.messageType === ResultMessageType.NoData &&
        typeWellSeries.length === 0) ||
      !apiResponse);

  const isFullscreenActive = fullScreen?.active;
  const isScreenshotActive = screenshot?.active;
  const isDashboardMode = useIsDashboardScreenshotMode(id);
  // In dashboard screenshot mode, legendVisible from settings overrides chartLegend.active
  const showChartLegend = isDashboardMode
    ? !dataTable?.active && screenshot?.properties?.preset?.legendVisible !== false
    : chartLegend?.active && !screenshot?.active && !dataTable?.active;

  // custom hooks
  useChartSize(id, wrapperRef);
  useEffect(() => onFullscreen(isFullscreenActive), [isFullscreenActive]);
  useChartModificationTracker(id);
  useTypeWellNormalizationToasts({
    id,
    numberOfTypeWellsMissingNormalization,
    normalizeBy,
    useNormalizeBy,
    checkedGlobalTypeWells,
    hasTypeWellSaveSucceeded
  });

  // Seeded from initialChartSettings (dashboard or session settings). EvaChart.None
  // is a "no entity" sentinel older snapshots persisted, not a chart type.
  const chartType =
    !initialChartSettings?.chartType || initialChartSettings.chartType === EvaChart.None
      ? entityKind === EntityKind.Well
        ? EvaChart.RateCum
        : EvaChart.RateDateMidstream
      : initialChartSettings.chartType;

  useEffect(() => {
    // Check if this is a different dashboard/chart or first load
    const isNewDashboard =
      !prevInitialSettingsRef.current ||
      prevInitialSettingsRef.current.chartType !== initialChartSettings.chartType ||
      prevInitialSettingsRef.current.id !== id;

    if (isNewDashboard) {
      let initialProduct = initialChartSettings.product;
      if (checkProductExists(initialChartSettings.product, entityKind)) {
        initialProduct = getProductType(product, entityKind).label;
      }
      // Apply settings only if it's a new dashboard or first load
      const updatedFeatures = createFeatureEntities(
        chartType,
        initialChartSettings.features
      );
      restoreNormalizationUnitsSentinel(
        updatedFeatures,
        initialChartSettings?.features?.normalization?.properties?.units
      );
      dispatch(
        updateChart({
          id,
          features: updatedFeatures,
          product: initialProduct,
          chartType,
          locks: initialChartSettings.locks,
          mpcLocks: initialChartSettings.mpcLocks ?? UNDEFINED_MPC_LOCKS
        })
      );

      // Update our reference to current settings
      prevInitialSettingsRef.current = { ...initialChartSettings, id };
    }
  }, [id, initialChartSettings]); // Keep both dependencies

  // used to show/hide the indicator when the chart is idle
  useEffect(() => {
    if (status === ChartStatus.Idle) {
      noDataIndicatorRef.current = isMissingData && !countExceeded;
      wellsHiddenIndicatorRef.current =
        entityKind === EntityKind.Well &&
        !activeEntities.includes(EntityKind.Well) &&
        typeWellSeries.length === 0;
    }
  }, [
    status,
    isMissingData,
    countExceeded,
    entityKind,
    activeEntities,
    typeWellSeries.length
  ]);

  const isTypeWellChecked = typeWellSeries.length > 0;

  const isTotalRateDate =
    entityChartType === EvaChart.TotalRateDate ||
    entityChartType === EvaChart.TotalRateDateMidstream;
  const product =
    initialChartSettings?.product ??
    (entityKind === EntityKind.Well
      ? DEFAULT_CHART_PRODUCT
      : DEFAULT_MIDSTREAM_CHART_PRODUCT);
  const locks = initialChartSettings?.locks ?? UNDEFINED_LOCKS;
  const mpcLocks = initialChartSettings?.mpcLocks ?? UNDEFINED_MPC_LOCKS;
  const features = createFeatureEntities(chartType, {
    ...initialChartSettings?.features
  });
  restoreNormalizationUnitsSentinel(
    features,
    initialChartSettings?.features?.normalization?.properties?.units
  );

  // Initial chart creation upon first render
  useEffect(() => {
    dispatch(
      createChart({
        id,
        ...createEntityState(EntityType.Chart, {
          id,
          chartType,
          product,
          features,
          locks,
          mpcLocks
        })
      })
    );

    return () => {
      dispatch(removeChart(id));
    };
  }, [id, dispatch]);

  const axisClassnames = classnames({
    visible: isHover && !isScreenshotActive
  });

  // Safety-net: clear hover state when the pointer leaves the chart boundary.
  // Debounced (see above) to avoid firing at pointer frequency.
  // setAttentionWells is intentionally NOT dispatched here — that reset is
  // already handled with a 400 ms debounce by useChartMouseInteractions
  // handleGlobalOut, preventing redundant Mapbox querySourceFeatures calls.
  // Register this chart's hover state in widgetHoverAtom keyed by its own `id` —
  // the same key every chart hook reads as `isHover`. In the Explore view the
  // chart is NOT wrapped by WidgetWrapper/PopoutChart (the only other writers of
  // widgetHoverAtom), so without this the flag is permanently false and every
  // `!isHover -> HideTip` guard is dead — which is why the tooltip stuck on
  // chart->map / chart->chart transitions here. Mirrors PopoutChart's wiring. (EVA-6690)
  const handleMouseEnter = () => {
    setWidgetHover({ widgetId: id, isHover: true });
  };

  const handleMouseLeave = () => {
    setWidgetHover({ widgetId: id, isHover: false });
    debouncedClearHoverLegend();
  };

  return (
    <ErrorBoundary>
      <Wrapper
        ref={wrapperRef} // for useChartSize
        className={rootClassnames}
        data-testid="chart-wrapper"
        // The dashboard can hold several charts and nothing else in the markup
        // tells them apart — no id, no index, and the widget title renders only
        // in design mode. The guided walkthrough needs to point at a specific
        // one ("the Rate Cum chart"), and the type is otherwise only in a
        // tooltip, which antd renders on hover in a portal.
        data-chart-type={entityChartType}
        // Normalization state belongs here, not on the toolbar button that
        // shows it. `NormalizeByToggle` returns null unless `normalizeBy.show`,
        // and `ChartToolbar` slices overflowing buttons out of the DOM entirely
        // rather than hiding them — so a selector reaching for that button
        // misses on a narrow chart or one that does not offer normalization.
        data-normalized={Boolean(normalizeBy?.active)}
        $screenshot={isScreenshotActive}
        $isDashboardMode={isDashboardMode}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        $useGreyMidstreamChartBackground={
          midstreamChartBackgroundColour?.useGreyBackgroundColour &&
          entityKind === EntityKind.Facility
        }>
        <ChartContainer $screenshot={isScreenshotActive}>
          <Chart />
        </ChartContainer>

        {status === ChartStatus.Idle && isMissingData && (
          <Indicator message={"No Data Available"} />
        )}

        {status === ChartStatus.Idle && isServerError && (
          <Indicator message={serverErrorMessage || "Failed to Load Chart Data"} />
        )}

        {entityKind === EntityKind.Facility && !midstreamEnabled && (
          <Indicator message={"Midstream is disabled"} />
        )}

        {(status === ChartStatus.Idle
          ? entityKind === EntityKind.Well &&
            !activeEntities.includes(EntityKind.Well) &&
            !isTypeWellChecked
          : wellsHiddenIndicatorRef.current) && (
          <Indicator message={"Wells are hidden"} />
        )}

        {entityKind === EntityKind.Facility &&
          midstreamEnabled &&
          !activeEntities.includes(EntityKind.Facility) && (
            <Indicator message={"Midstream is hidden"} />
          )}

        <ChartToolbar />

        {showChartLegend && bounds?.width > 0 && (
          <ChartLegend
            parentDimensions={{ width: bounds.width, height: bounds.height }}
            id={screenshotContainerId + "chart-legend"}
            screenshot={screenshot}
            bounds={bounds}
            legend={chartLegend}
            showLegendBorder={
              isDashboardMode
                ? !!screenshot?.properties?.preset?.legendBorderVisible
                : undefined
            }
          />
        )}

        {disableAxisLocks(entityChartType) && <AxisContainer />}
        <ChartAxisFieldSelector type={AXIS_TYPE.x} className={axisClassnames} />
        <ChartAxisFieldSelector type={AXIS_TYPE.y} className={axisClassnames} />
        {IS_BOX_PLOT_CHART(entityChartType) && <BoxPlotBinSettingsToggle />}

        <Screenshot containerId={screenshotContainerId} />

        {isTotalRateDate && <WellCountToggle type={entityKind} />}

        <ChartAutomationHandler />

        <TypeWellCutoffToastNotifier
          chartId={id}
          currentProduct={entityProduct}
          chartType={entityChartType}
        />

        <CopyUwiContextMenu />
      </Wrapper>
    </ErrorBoundary>
  );
}

export default ChartWrapper;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  border-radius: var(--border-radius);
  background-color: ${(props) =>
    props.$screenshot && !props.$isDashboardMode ? "rgba(46, 72, 88, 0.24)" : "inherit"};
  box-shadow: ${(props) =>
    props.$isDashboardMode ? "none" : "rgba(var(--color-shadow-rgb), 0.2) 0 0 1px"};
  padding-top: 10px;
  z-index: 0;
  background: ${(props) =>
    props.$useGreyMidstreamChartBackground && !props.$screenshot
      ? MIDSTREAM_CHART_BACKGROUND_COLOUR
      : ""};

  &.hovered {
    box-shadow: rgba(var(--color-shadow-rgb), 0.4) 0 0 1px;
  }

  &.fullscreen {
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: var(--index-fullscreen);
  }
`;

const ChartContainer = styled.div`
  justify-self: ${(props) => (props.$screenshot ? "center" : "auto")};

  &:hover {
    z-index: 1;
  }
`;
